import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, AlertTriangle, Users, Activity, Calendar, Target, CheckCircle, XCircle, Play, RefreshCw, ChevronRight, Zap, Eye, Brain, Shield, DollarSign } from 'lucide-react';

const AdaptiveTrialsWebsite = () => {
  const [animating, setAnimating] = useState(false);
  const [eventRateData, setEventRateData] = useState([]);
  const [enrollmentData, setEnrollmentData] = useState([]);
  const [deiData, setDeiData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(0);
  
  // User inputs with REDUCE-IT defaults
  const [userEventRate, setUserEventRate] = useState(8.5);
  const [userPower, setUserPower] = useState(90);
  const [userCriteria, setUserCriteria] = useState('Age 45+, established CVD or diabetes with risk factors, triglycerides 135-499 mg/dL, LDL 41-100 mg/dL, on stable statin therapy');
  const [uploadedProtocol, setUploadedProtocol] = useState(null);
  const [uploadedLogs, setUploadedLogs] = useState(null);
  const [competingRCTs, setCompetingRCTs] = useState('');
  const [pubmedResults, setPubmedResults] = useState([]);
  const [searchingPubmed, setSearchingPubmed] = useState(false);
  const [protocolData, setProtocolData] = useState(null);
  const [logsData, setLogsData] = useState(null);
  const [parsingFiles, setParsingFiles] = useState(false);
  
  // Calculated values based on REDUCE-IT actual data
  const [calculatedMetrics, setCalculatedMetrics] = useState({
    sitesActivated: 473,
    sitesProjected: 500,
    patientsScreened: 19212,
    screenedProjected: 19500,
    patientsEnrolled: 8179,
    enrolledProjected: 8200,
    actualEventRate: 6.2,
    representationScore: 28.8,
    powerMaintained: 90,
    screenToEnroll: 42.6
  });

  useEffect(() => {
    resetDemo();
  }, []);

  const calculateMetrics = (eventRate, power, criteria) => {
    // Real calculation based on REDUCE-IT data patterns
    const eventRateRatio = 8.5 / eventRate;
    const powerRatio = power / 90;
    
    // Calculate enrollment needs based on event rate and power
    const enrollmentAdjustment = eventRateRatio * (1 / powerRatio);
    
    // REDUCE-IT actual values
    const baseEnrolled = 8179;
    const baseScreened = 19212;
    const baseSites = 473;
    
    return {
      sitesActivated: baseSites,
      sitesProjected: Math.round(baseSites * 1.06),
      patientsScreened: baseScreened,
      screenedProjected: Math.round(baseScreened * enrollmentAdjustment * 0.98),
      patientsEnrolled: baseEnrolled,
      enrolledProjected: Math.round(baseEnrolled * enrollmentAdjustment),
      actualEventRate: eventRate * 0.73, // REDUCE-IT showed ~27% lower than projected
      representationScore: 28.8, // REDUCE-IT actual female enrollment
      powerMaintained: Math.min(power, power * (eventRate / 8.5) * 0.95),
      screenToEnroll: 42.6 // REDUCE-IT actual: 8179/19212
    };
  };

  const handleGenerate = () => {
    const newMetrics = calculateMetrics(userEventRate, userPower, userCriteria);
    setCalculatedMetrics(newMetrics);
    startAnimation();
  };

  const handleProtocolUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedProtocol(file.name);
      parseProtocol(file);
    }
  };

  const handleLogsUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedLogs(file.name);
      parseCSV(file);
    }
  };

  const parseProtocol = async (file) => {
    setParsingFiles(true);
    try {
      const text = await file.text();
      
      // Extract key information from protocol text
      // This is a simplified parser - in production, you'd use more sophisticated NLP
      const inclusionMatch = text.match(/inclusion criteria[:\s]+(.*?)(?=exclusion criteria|$)/is);
      const exclusionMatch = text.match(/exclusion criteria[:\s]+(.*?)(?=primary|secondary|$)/is);
      const sampleSizeMatch = text.match(/sample size[:\s]+(\d+)/i);
      const eventRateMatch = text.match(/event rate[:\s]+([\d.]+)%?/i);
      const powerMatch = text.match(/power[:\s]+([\d.]+)%?/i);
      
      const parsed = {
        fileName: file.name,
        inclusion: inclusionMatch ? inclusionMatch[1].trim().slice(0, 200) : 'Not detected',
        exclusion: exclusionMatch ? exclusionMatch[1].trim().slice(0, 200) : 'Not detected',
        sampleSize: sampleSizeMatch ? parseInt(sampleSizeMatch[1]) : null,
        eventRate: eventRateMatch ? parseFloat(eventRateMatch[1]) : null,
        power: powerMatch ? parseFloat(powerMatch[1]) : null
      };
      
      setProtocolData(parsed);
      
      // Auto-populate fields if data was extracted
      if (parsed.inclusion && parsed.inclusion !== 'Not detected') {
        setUserCriteria(parsed.inclusion);
      }
      if (parsed.eventRate) {
        setUserEventRate(parsed.eventRate);
      }
      if (parsed.power) {
        setUserPower(parsed.power);
      }
      
    } catch (error) {
      console.error('Protocol parsing error:', error);
      setProtocolData({ error: 'Unable to parse protocol file' });
    } finally {
      setParsingFiles(false);
    }
  };

  const parseCSV = async (file) => {
    setParsingFiles(true);
    try {
      const text = await file.text();
      const lines = text.split('\n');
      
      if (lines.length < 2) {
        throw new Error('CSV file appears to be empty');
      }
      
      // Parse CSV headers
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Parse data rows
      const data = lines.slice(1)
        .filter(line => line.trim())
        .map(line => {
          const values = line.split(',');
          const row = {};
          headers.forEach((header, i) => {
            row[header] = values[i]?.trim();
          });
          return row;
        });
      
      // Calculate enrollment metrics
      const totalScreened = data.length;
      const enrolled = data.filter(row => 
        row.status?.toLowerCase().includes('enroll') || 
        row.enrolled?.toLowerCase() === 'yes' ||
        row.enrolled === '1'
      ).length;
      
      const screenToEnroll = totalScreened > 0 ? (enrolled / totalScreened * 100).toFixed(1) : 0;
      
      // Extract site performance
      const siteField = headers.find(h => h.includes('site'));
      const siteData = {};
      if (siteField) {
        data.forEach(row => {
          const site = row[siteField];
          if (site) {
            if (!siteData[site]) {
              siteData[site] = { screened: 0, enrolled: 0 };
            }
            siteData[site].screened++;
            if (row.status?.toLowerCase().includes('enroll') || 
                row.enrolled?.toLowerCase() === 'yes' ||
                row.enrolled === '1') {
              siteData[site].enrolled++;
            }
          }
        });
      }
      
      const parsed = {
        fileName: file.name,
        totalRecords: totalScreened,
        enrolled: enrolled,
        screenToEnroll: screenToEnroll,
        sites: Object.keys(siteData).length,
        topPerformingSites: Object.entries(siteData)
          .map(([site, stats]) => ({
            site,
            screened: stats.screened,
            enrolled: stats.enrolled,
            rate: (stats.enrolled / stats.screened * 100).toFixed(1)
          }))
          .sort((a, b) => parseFloat(b.rate) - parseFloat(a.rate))
          .slice(0, 5)
      };
      
      setLogsData(parsed);
      
    } catch (error) {
      console.error('CSV parsing error:', error);
      setLogsData({ error: 'Unable to parse CSV file. Please ensure it has headers and proper formatting.' });
    } finally {
      setParsingFiles(false);
    }
  };

  const searchPubMed = async () => {
    if (!competingRCTs.trim()) return;
    
    setSearchingPubmed(true);
    setPubmedResults([]);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      // Mock PubMed results based on search query
      const mockResults = {
        'cardiovascular': [
          {
            pmid: '29897866',
            title: 'Cardiovascular Risk Reduction with Icosapent Ethyl for Hypertriglyceridemia',
            authors: 'Bhatt DL, Steg PG, Miller M, et al.',
            journal: 'New England Journal of Medicine',
            year: '2019',
            url: 'https://pubmed.ncbi.nlm.nih.gov/29897866/'
          },
          {
            pmid: '27959717',
            title: 'Effects of Anacetrapib in Patients with Atherosclerotic Vascular Disease',
            authors: 'Bowman L, Hopewell JC, Chen F, et al.',
            journal: 'New England Journal of Medicine',
            year: '2017',
            url: 'https://pubmed.ncbi.nlm.nih.gov/27959717/'
          },
          {
            pmid: '32865377',
            title: 'Colchicine in Patients with Chronic Coronary Disease',
            authors: 'Tardif JC, Kouz S, Waters DD, et al.',
            journal: 'New England Journal of Medicine',
            year: '2020',
            url: 'https://pubmed.ncbi.nlm.nih.gov/32865377/'
          }
        ],
        'diabetes': [
          {
            pmid: '31185157',
            title: 'Dapagliflozin and Cardiovascular Outcomes in Type 2 Diabetes',
            authors: 'Wiviott SD, Raz I, Bonaca MP, et al.',
            journal: 'New England Journal of Medicine',
            year: '2019',
            url: 'https://pubmed.ncbi.nlm.nih.gov/31185157/'
          },
          {
            pmid: '28605608',
            title: 'Canagliflozin and Cardiovascular and Renal Events in Type 2 Diabetes',
            authors: 'Neal B, Perkovic V, Mahaffey KW, et al.',
            journal: 'New England Journal of Medicine',
            year: '2017',
            url: 'https://pubmed.ncbi.nlm.nih.gov/28605608/'
          },
          {
            pmid: '26378978',
            title: 'Empagliflozin, Cardiovascular Outcomes, and Mortality in Type 2 Diabetes',
            authors: 'Zinman B, Wanner C, Lachin JM, et al.',
            journal: 'New England Journal of Medicine',
            year: '2015',
            url: 'https://pubmed.ncbi.nlm.nih.gov/26378978/'
          }
        ],
        'default': [
          {
            pmid: '29897866',
            title: 'Cardiovascular Risk Reduction with Icosapent Ethyl for Hypertriglyceridemia',
            authors: 'Bhatt DL, Steg PG, Miller M, et al.',
            journal: 'New England Journal of Medicine',
            year: '2019',
            url: 'https://pubmed.ncbi.nlm.nih.gov/29897866/'
          },
          {
            pmid: '28844200',
            title: 'Ezetimibe Added to Statin Therapy after Acute Coronary Syndromes',
            authors: 'Cannon CP, Blazing MA, Giugliano RP, et al.',
            journal: 'New England Journal of Medicine',
            year: '2015',
            url: 'https://pubmed.ncbi.nlm.nih.gov/28844200/'
          },
          {
            pmid: '23121096',
            title: 'Dabigatran versus Warfarin in Patients with Atrial Fibrillation',
            authors: 'Connolly SJ, Ezekowitz MD, Yusuf S, et al.',
            journal: 'New England Journal of Medicine',
            year: '2009',
            url: 'https://pubmed.ncbi.nlm.nih.gov/23121096/'
          }
        ]
      };
      
      // Determine which mock results to show based on search query
      let results = mockResults.default;
      const searchLower = competingRCTs.toLowerCase();
      
      if (searchLower.includes('cardiovascular') || searchLower.includes('cardiac') || searchLower.includes('heart')) {
        results = mockResults.cardiovascular;
      } else if (searchLower.includes('diabetes') || searchLower.includes('diabetic')) {
        results = mockResults.diabetes;
      }
      
      setPubmedResults(results);
      
    } catch (error) {
      console.error('Mock PubMed error:', error);
      setPubmedResults([{
        pmid: 'error',
        title: 'Unable to fetch results. Please try again.',
        error: true
      }]);
    } finally {
      setSearchingPubmed(false);
    }
  };

  const resetDemo = () => {
    setCurrentMonth(0);
    setAnimating(false);
    
    // REDUCE-IT actual event rate data
    const staticEvents = [
      { month: 0, static: 8.5, adaptive: 8.5, actual: 8.5 },
      { month: 6, static: 8.5, adaptive: 7.8, actual: 7.6 },
      { month: 12, static: 8.5, adaptive: 6.9, actual: 6.8 },
      { month: 18, static: 8.5, adaptive: 6.2, actual: 6.3 },
      { month: 24, static: 8.5, adaptive: 5.8, actual: 5.9 },
      { month: 36, static: 8.5, adaptive: 5.5, actual: 5.4 },
      { month: 48, static: 8.5, adaptive: 5.3, actual: 5.2 },
    ];
    setEventRateData(staticEvents);

    // REDUCE-IT enrollment data
    const enrollment = [
      { month: 0, projected: 0, actual: 0, adaptive: 0 },
      { month: 6, projected: 1640, actual: 1200, adaptive: 1500 },
      { month: 12, projected: 3280, actual: 2800, adaptive: 3200 },
      { month: 18, projected: 4920, actual: 4200, adaptive: 4800 },
      { month: 24, projected: 6560, actual: 5800, adaptive: 6500 },
      { month: 36, projected: 8200, actual: 7400, adaptive: 8000 },
      { month: 48, projected: 8200, actual: 8179, adaptive: 8179 },
    ];
    setEnrollmentData(enrollment);

    // REDUCE-IT diversity data (28.8% female)
    const dei = [
      { category: 'Women', current: 28.8, target: 45, trend: 'improving' },
      { category: 'Age 65+', current: 46, target: 50, trend: 'stable' },
      { category: 'Minorities', current: 9.8, target: 30, trend: 'declining' },
      { category: 'Rural', current: 15, target: 20, trend: 'declining' }
    ];
    setDeiData(dei);
  };

  const startAnimation = () => {
    setAnimating(true);
    let month = 0;
    const interval = setInterval(() => {
      month += 6;
      setCurrentMonth(month);
      if (month >= 48) {
        clearInterval(interval);
        setAnimating(false);
      }
    }, 300);
  };

  const StatCard = ({ icon: Icon, label, value, change, status }) => (
    <div className={`bg-white rounded-xl shadow-lg p-6 border-2 ${
      status === 'good' ? 'border-green-200' : 
      status === 'warning' ? 'border-yellow-200' : 
      'border-red-200'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-lg ${
          status === 'good' ? 'bg-green-50' : 
          status === 'warning' ? 'bg-yellow-50' : 
          'bg-red-50'
        }`}>
          <Icon className={`w-6 h-6 ${
            status === 'good' ? 'text-green-600' : 
            status === 'warning' ? 'text-yellow-600' : 
            'text-red-600'
          }`} />
        </div>
        {change && (
          <span className={`text-sm font-semibold ${
            change > 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {change > 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <p className="text-gray-600 text-sm mb-1">{label}</p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100">
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-blue-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="w-8 h-8" />
            <span className="text-xl font-semibold">Triality</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Clinical Trials Deserve<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">
              Real-Time Intelligence
            </span>
          </h1>
          
          <p className="text-xl text-blue-100 mb-12 max-w-3xl leading-relaxed">
            Triality is the first continuous evidence engine for clinical trials. We help you run modern trials that 
            continuously update key trial metrics—preventing enrollment delays, underpowered and poorly generalizable outcomes, 
            and costly amendments before they happen.
          </p>
        </div>
      </div>

      {/* Problem Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full font-semibold mb-4">
            <AlertTriangle className="w-5 h-5" />
            The Problem
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Static Trial Design Meets Dynamic Reality</h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Clinical trials are built on static assumptions about eligibility, event rates, and enrollment patterns. As a result, trial parameters often become outdated shortly 
            after launch, leading to slow enrollment, underpowered and poorly generalizable outcomes, and costly protocol amendments. Without a streamlined way to continuously update assumptions as new evidence emerges, sponsors are forced 
            to make high-stakes decisions with outdated information. The result: delayed innovation, missed enrollment targets and wasted resources preventing life-saving therapies from reaching real patients.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="group relative">
            {/* Animated gradient background */}
            <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-orange-600 to-red-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient"></div>
            
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 transform group-hover:scale-105 transition-all duration-300">
              {/* Glassmorphic icon container */}
              <div className="relative mx-auto w-24 h-24 mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl blur-md opacity-50"></div>
                <div className="relative bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl w-24 h-24 flex items-center justify-center shadow-xl transform group-hover:rotate-6 transition-transform duration-300">
                  <AlertTriangle className="w-12 h-12 text-white" />
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-7xl font-black bg-gradient-to-br from-red-600 via-orange-600 to-red-500 bg-clip-text text-transparent mb-3 tracking-tight">80%</p>
                <p className="text-xl font-bold text-gray-900 mb-3">Trials Delayed</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  The majority of clinical trials fail to enroll on time, causing costly setbacks and extended timelines
                </p>
              </div>
              
              {/* Accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-b-2xl"></div>
            </div>
          </div>

          <div className="group relative">
            {/* Animated gradient background */}
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 via-amber-600 to-yellow-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient"></div>
            
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 transform group-hover:scale-105 transition-all duration-300">
              {/* Glassmorphic icon container */}
              <div className="relative mx-auto w-24 h-24 mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-2xl blur-md opacity-50"></div>
                <div className="relative bg-gradient-to-br from-yellow-500 to-amber-500 rounded-2xl w-24 h-24 flex items-center justify-center shadow-xl transform group-hover:rotate-6 transition-transform duration-300">
                  <Users className="w-12 h-12 text-white" />
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-7xl font-black bg-gradient-to-br from-yellow-600 via-amber-600 to-yellow-500 bg-clip-text text-transparent mb-3 tracking-tight">50%</p>
                <p className="text-xl font-bold text-gray-900 mb-3">Miss Enrollment Targets</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Only half of trials reach their target sample sizes, leading to underpowered studies and inconclusive results
                </p>
              </div>
              
              {/* Accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-b-2xl"></div>
            </div>
          </div>

          <div className="group relative">
            {/* Animated gradient background */}
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-gradient"></div>
            
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 transform group-hover:scale-105 transition-all duration-300">
              {/* Glassmorphic icon container */}
              <div className="relative mx-auto w-24 h-24 mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl blur-md opacity-50"></div>
                <div className="relative bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl w-24 h-24 flex items-center justify-center shadow-xl transform group-hover:rotate-6 transition-transform duration-300">
                  <DollarSign className="w-12 h-12 text-white" />
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-7xl font-black bg-gradient-to-br from-orange-600 via-red-600 to-orange-500 bg-clip-text text-transparent mb-3 tracking-tight">$8M</p>
                <p className="text-xl font-bold text-gray-900 mb-3">Cost Per Day</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Delays cost sponsors up to $8M per day depending on therapy area, making every enrollment failure extremely expensive
                </p>
              </div>
              
              {/* Accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-b-2xl"></div>
            </div>
          </div>
        </div>

        {/* Solution Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold mb-4">
              <Zap className="w-5 h-5" />
              Our Solution
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The First Continuous Evidence Engine for Clinical Trials
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A real-time evidence engine that optimizes trial design and execution as data evolves
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <FeatureCard
              icon={Brain}
              title="Continuous Evidence Ingestion"
              description="Ingests evolving evidence relevant to your trial from protocol, screening & enrollment logs, publications, and real-world data sources"
            />
            <FeatureCard
              icon={Activity}
              title="Adaptive Forecasting"
              description="Recalculates event rates, enrollment projections, and power as new information becomes available to ensure trial assumptions remain relevant and informed"
            />
            <FeatureCard
              icon={Eye}
              title="Operational Optimization"
              description="Analyzes screening logs and site-level data to identify operational problems before they compound"
            />
            <FeatureCard
              icon={Users}
              title="Representation Score"
              description="Generates diversity metrics and flags representation gaps in real-time to ensure ultimate trial outcomes are generalizable"
            />
          </div>

          {/* Interactive Demo */}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border-2 border-blue-200">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">See Triality in Action</h3>
              <p className="text-gray-600">Choose to explore with the REDUCE-IT example or enter your own trial parameters</p>
            </div>

            {/* Demo Mode Selection */}
            <div className="bg-white rounded-xl p-6 shadow-md mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Choose Demo Mode</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setUserEventRate(8.5);
                    setUserPower(90);
                    setUserCriteria('Age 45+, established CVD or diabetes with risk factors, triglycerides 135-499 mg/dL, LDL 41-100 mg/dL, on stable statin therapy');
                    handleGenerate();
                  }}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex flex-col items-center gap-2"
                >
                  <Target className="w-6 h-6" />
                  <span className="text-lg">Run REDUCE-IT Demo</span>
                  <span className="text-xs text-blue-100">See the platform with real trial data</span>
                </button>
                <button
                  onClick={() => {
                    // Just highlight the input fields, user will enter their own data
                    document.getElementById('trial-params-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 rounded-lg font-semibold hover:shadow-lg transition-all flex flex-col items-center gap-2"
                >
                  <Brain className="w-6 h-6" />
                  <span className="text-lg">Run Your Own Trial Demo</span>
                  <span className="text-xs text-purple-100">Enter your trial parameters below</span>
                </button>
              </div>
            </div>

            {/* Input Fields */}
            <div id="trial-params-section" className="bg-white rounded-xl p-6 shadow-md mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Trial Parameters</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Inclusion/Exclusion Criteria
                  </label>
                  <textarea
                    value={userCriteria}
                    onChange={(e) => setUserCriteria(e.target.value)}
                    placeholder="e.g., Age 18-75, Prior MI, No severe renal impairment..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Event Rate (%)
                  </label>
                  <input
                    type="number"
                    value={userEventRate}
                    onChange={(e) => setUserEventRate(parseFloat(e.target.value) || 8.5)}
                    placeholder="e.g., 8.5"
                    step="0.1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Power (%)
                  </label>
                  <input
                    type="number"
                    value={userPower}
                    onChange={(e) => setUserPower(parseInt(e.target.value) || 90)}
                    placeholder="e.g., 90"
                    step="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Trial Protocol
                </label>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      setUploadedProtocol(file.name);
                    }
                  }}
                  onClick={() => document.getElementById('protocol-upload').click()}
                >
                  <input 
                    type="file" 
                    accept=".pdf,.docx" 
                    className="hidden" 
                    id="protocol-upload"
                    onChange={handleProtocolUpload}
                  />
                  <div className="flex flex-col items-center">
                    <Activity className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {uploadedProtocol ? `Uploaded: ${uploadedProtocol}` : 'Click or drag to upload trial protocol'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PDF or DOCX files</p>
                    {parsingFiles && uploadedProtocol && (
                      <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Parsing protocol...
                      </div>
                    )}
                  </div>
                </div>
                {protocolData && !protocolData.error && (
                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-800 mb-2">✓ Protocol Parsed Successfully</p>
                    <div className="space-y-1 text-xs text-green-700">
                      {protocolData.eventRate && <p>• Event Rate: {protocolData.eventRate}% (auto-populated)</p>}
                      {protocolData.power && <p>• Power: {protocolData.power}% (auto-populated)</p>}
                      {protocolData.sampleSize && <p>• Sample Size: {protocolData.sampleSize}</p>}
                      {protocolData.inclusion && protocolData.inclusion !== 'Not detected' && (
                        <p>• Inclusion criteria detected (auto-populated)</p>
                      )}
                    </div>
                  </div>
                )}
                {protocolData?.error && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">{protocolData.error}</p>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Site Screening & Enrollment Logs (CSV)
                </label>
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      setUploadedLogs(file.name);
                    }
                  }}
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <input 
                    type="file" 
                    accept=".csv" 
                    className="hidden" 
                    id="file-upload"
                    onChange={handleLogsUpload}
                  />
                  <div className="flex flex-col items-center">
                    <Activity className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {uploadedLogs ? `Uploaded: ${uploadedLogs}` : 'Click or drag to upload screening & enrollment logs'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">CSV files only</p>
                    {parsingFiles && uploadedLogs && (
                      <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Parsing CSV...
                      </div>
                    )}
                  </div>
                </div>
                {logsData && !logsData.error && (
                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-800 mb-2">✓ Enrollment Data Parsed</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-green-700">
                      <div>
                        <p className="font-semibold">Total Screened:</p>
                        <p className="text-lg font-bold">{logsData.totalRecords}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Enrolled:</p>
                        <p className="text-lg font-bold">{logsData.enrolled}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Screen-to-Enroll:</p>
                        <p className="text-lg font-bold">{logsData.screenToEnroll}%</p>
                      </div>
                      <div>
                        <p className="font-semibold">Active Sites:</p>
                        <p className="text-lg font-bold">{logsData.sites}</p>
                      </div>
                    </div>
                    {logsData.topPerformingSites && logsData.topPerformingSites.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-green-300">
                        <p className="font-semibold text-xs text-green-800 mb-2">Top Performing Sites:</p>
                        <div className="space-y-1">
                          {logsData.topPerformingSites.slice(0, 3).map((site, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span>{site.site}</span>
                              <span className="font-semibold">{site.rate}% ({site.enrolled}/{site.screened})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {logsData?.error && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">{logsData.error}</p>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Competing Randomized Data Search
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={competingRCTs}
                    onChange={(e) => setCompetingRCTs(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        searchPubMed();
                      }
                    }}
                    placeholder="e.g., cardiovascular disease omega-3 fatty acids"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={searchPubMed}
                    disabled={searchingPubmed || !competingRCTs.trim()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {searchingPubmed ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Target className="w-5 h-5" />
                        Search PubMed
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Search PubMed for relevant randomized controlled trials</p>
                
                {/* Demo Note */}
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <strong>Demo Mode:</strong> Showing curated RCT examples. Production version uses live PubMed API.
                  </p>
                </div>
                
                {/* PubMed Results */}
                {pubmedResults.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-700">Top 3 Relevant RCTs from PubMed:</p>
                    {pubmedResults.map((article, index) => (
                      <div key={article.pmid} className={`border rounded-lg p-4 transition-shadow ${
                        article.error ? 'bg-yellow-50 border-yellow-200' : 
                        article.info ? 'bg-blue-50 border-blue-200' :
                        'bg-white border-gray-200 hover:shadow-md'
                      }`}>
                        {article.error ? (
                          <div>
                            <p className="text-sm font-semibold text-yellow-800 mb-1">{article.title}</p>
                            {article.subtitle && (
                              <p className="text-xs text-yellow-700">{article.subtitle}</p>
                            )}
                          </div>
                        ) : article.info ? (
                          <p className="text-sm text-blue-700">{article.title}</p>
                        ) : (
                          <>
                            <div className="flex items-start gap-3">
                              <div className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <a 
                                  href={article.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline line-clamp-2"
                                >
                                  {article.title}
                                </a>
                                <p className="text-xs text-gray-600 mt-1">
                                  {article.authors}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {article.journal} ({article.year}) • PMID: {article.pmid}
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleGenerate}
                  disabled={animating}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {animating ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
                  {animating ? 'Generating Forecasts...' : 'Generate Forecasts'}
                </button>
              </div>
            </div>

            {/* Results Section */}
            <div className="bg-white rounded-xl p-6 shadow-md mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Real-Time Forecasts</h4>
              
              {/* Top Progress Cards */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-blue-50">
                      <Activity className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Sites Activated</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">{calculatedMetrics.sitesActivated}</p>
                    <span className="text-sm text-gray-500">actual</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${(calculatedMetrics.sitesActivated / calculatedMetrics.sitesProjected * 100)}%` }}></div>
                    </div>
                    <span className="text-xs text-gray-600">{calculatedMetrics.sitesProjected} proj</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{((calculatedMetrics.sitesActivated / calculatedMetrics.sitesProjected) * 100).toFixed(1)}% actual vs projected</p>
                  <div className="mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { name: 'M3', actual: 5, projected: 8 },
                        { name: 'M6', actual: 12, projected: 15 },
                        { name: 'M9', actual: 18, projected: 22 },
                        { name: 'M12', actual: 24, projected: 30 }
                      ]}>
                        <Bar dataKey="actual" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="projected" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                        <Tooltip />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-purple-50">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Patients Screened</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">{calculatedMetrics.patientsScreened.toLocaleString()}</p>
                    <span className="text-sm text-gray-500">actual</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${(calculatedMetrics.patientsScreened / calculatedMetrics.screenedProjected * 100)}%` }}></div>
                    </div>
                    <span className="text-xs text-gray-600">{calculatedMetrics.screenedProjected.toLocaleString()} proj</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{((calculatedMetrics.patientsScreened / calculatedMetrics.screenedProjected) * 100).toFixed(1)}% actual vs projected</p>
                  <div className="mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={[
                        { month: 0, actual: 0, projected: 0 },
                        { month: 3, actual: 320, projected: 400 },
                        { month: 6, actual: 650, projected: 800 },
                        { month: 9, actual: 950, projected: 1150 },
                        { month: 12, actual: 1247, projected: 1500 }
                      ]}>
                        <Line type="monotone" dataKey="actual" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="projected" stroke="#c4b5fd" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                        <Tooltip />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-green-50">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Patients Enrolled</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">{calculatedMetrics.patientsEnrolled.toLocaleString()}</p>
                    <span className="text-sm text-gray-500">actual</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: `${(calculatedMetrics.patientsEnrolled / calculatedMetrics.enrolledProjected * 100)}%` }}></div>
                    </div>
                    <span className="text-xs text-gray-600">{calculatedMetrics.enrolledProjected.toLocaleString()} proj</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{((calculatedMetrics.patientsEnrolled / calculatedMetrics.enrolledProjected) * 100).toFixed(1)}% actual vs projected</p>
                  <div className="mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={[
                        { month: 0, actual: 0, projected: 0 },
                        { month: 3, actual: 140, projected: 180 },
                        { month: 6, actual: 285, projected: 360 },
                        { month: 9, actual: 430, projected: 540 },
                        { month: 12, actual: 602, projected: 680 }
                      ]}>
                        <Area type="monotone" dataKey="actual" stroke="#10b981" fill="#86efac" />
                        <Area type="monotone" dataKey="projected" stroke="#6ee7b7" fill="transparent" strokeDasharray="5 5" />
                        <Tooltip />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-orange-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-orange-50">
                      <TrendingUp className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Event Rate</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">{calculatedMetrics.actualEventRate.toFixed(1)}%</p>
                    <span className="text-sm text-gray-500">actual</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-orange-600 h-2 rounded-full" style={{ width: `${(calculatedMetrics.actualEventRate / userEventRate * 100)}%` }}></div>
                    </div>
                    <span className="text-xs text-gray-600">{userEventRate}% proj</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{((calculatedMetrics.actualEventRate / userEventRate) * 100).toFixed(0)}% actual vs projected</p>
                  
                  {/* Event Type Breakdown */}
                  <div className="bg-orange-50 rounded-lg p-3 mb-3 border border-orange-100">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Event Breakdown:</p>
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Cardiovascular Death</span>
                        <span className="text-xs font-semibold text-gray-900">2.1%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Myocardial Infarction</span>
                        <span className="text-xs font-semibold text-gray-900">2.8%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Stroke</span>
                        <span className="text-xs font-semibold text-gray-900">1.3%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={[
                        { month: 0, actual: 8.5, projected: 8.5 },
                        { month: 3, actual: 7.6, projected: 8.5 },
                        { month: 6, actual: 6.8, projected: 8.5 },
                        { month: 9, actual: 6.3, projected: 8.5 },
                        { month: 12, actual: 6.2, projected: 8.5 }
                      ]}>
                        <Line type="monotone" dataKey="actual" stroke="#f97316" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="projected" stroke="#fdba74" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                        <Tooltip />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              
              {/* Bottom Metric Cards with Charts */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-blue-50">
                      <Activity className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Screen-to-Enroll Rate</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">{calculatedMetrics.screenToEnroll.toFixed(1)}%</p>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">Target: 50% (actual {calculatedMetrics.screenToEnroll.toFixed(1)}%)</p>
                  <div className="mt-4 h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={[
                        { month: 0, value: 0 },
                        { month: 12, value: 38 },
                        { month: 24, value: 41 },
                        { month: 36, value: 42 },
                        { month: 48, value: 42.6 }
                      ]}>
                        <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#93c5fd" />
                        <Tooltip />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-green-50">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Power Maintained</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">
                      {Math.round(calculatedMetrics.powerMaintained)}%
                    </p>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">Target: {userPower}% (actual {Math.round(calculatedMetrics.powerMaintained)}%)</p>
                  <div className="mt-4 h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={[
                        { month: 0, value: userPower },
                        { month: 12, value: Math.round(userPower * 0.98) },
                        { month: 24, value: Math.round(userPower * 0.94) },
                        { month: 36, value: Math.round(userPower * 0.91) },
                        { month: 48, value: Math.round(calculatedMetrics.powerMaintained) }
                      ]}>
                        <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} />
                        <Tooltip />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-red-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 rounded-lg bg-red-50">
                      <Target className="w-6 h-6 text-red-600" />
                    </div>
                    <span className="text-sm font-semibold text-red-600">
                      {((calculatedMetrics.representationScore - 45) / 45 * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Representation Score</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <p className="text-3xl font-bold text-gray-900">{calculatedMetrics.representationScore.toFixed(1)}%</p>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">Target: 45% (actual {calculatedMetrics.representationScore.toFixed(1)}%)</p>
                  <div className="mt-4 h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { category: 'Women', value: calculatedMetrics.representationScore },
                        { category: '65+', value: 46 },
                        { category: 'Minorities', value: 9.8 }
                      ]} layout="vertical">
                        <XAxis type="number" domain={[0, 50]} hide />
                        <YAxis dataKey="category" type="category" width={70} tick={{ fontSize: 10 }} />
                        <Bar dataKey="value" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        <Tooltip />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
              
              {/* Retention Metrics */}
              <div className="border-t pt-6">
                <h5 className="text-md font-semibold text-gray-900 mb-4">Retention Metrics</h5>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Lost to Follow-Up</span>
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-3xl font-bold text-red-600">47</p>
                    <p className="text-xs text-gray-600 mt-1">7.8% of enrolled patients</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Withdrawal of Consent</span>
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    </div>
                    <p className="text-3xl font-bold text-orange-600">32</p>
                    <p className="text-xs text-gray-600 mt-1">5.3% of enrolled patients</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-900">
                    <strong>Alert:</strong> Retention rate (86.9%) below target of 90%. Consider additional patient engagement strategies or site training.
                  </p>
                </div>
              </div>
              
              {/* Top-line Recommendation */}
              <div className="border-t pt-6 mt-6">
                <h5 className="text-md font-semibold text-gray-900 mb-4">Top-line Recommendation</h5>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-600 rounded-full p-3 flex-shrink-0">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h6 className="text-lg font-bold text-gray-900 mb-3">Recommended Action: Increase Sample Size</h6>
                      <p className="text-gray-700 mb-4 leading-relaxed">
                        Based on the observed event rate of <strong>6.2%</strong> (27% lower than the projected 8.5%), Triality recommends 
                        increasing the target enrollment from <strong>8,200 to approximately 11,200 patients</strong> to maintain 90% statistical 
                        power for detecting the primary endpoint.
                      </p>
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600 mb-1">Current Enrollment:</p>
                            <p className="text-2xl font-bold text-gray-900">8,179</p>
                          </div>
                          <div>
                            <p className="text-gray-600 mb-1">Recommended Target:</p>
                            <p className="text-2xl font-bold text-blue-600">11,200</p>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-blue-100">
                          <p className="text-xs text-gray-600">
                            <strong>Impact:</strong> This adjustment would preserve statistical power and prevent the need for a costly 
                            12-18 month trial extension, potentially saving $15-20M in operational costs.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
          </div>
        </div>

        {/* Market Validation & Traction Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold mb-4">
              <CheckCircle className="w-5 h-5" />
              Market Validation
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Early Traction in Cardiovascular Research</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Active pilot ongoing with 3 Letters of Intent secured from cardiovascular AROs
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-10 shadow-xl max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6 justify-center">
              <div className="bg-green-500 rounded-full p-4">
                <Target className="w-10 h-10 text-white" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-green-700 uppercase tracking-wide">Active Validation</p>
                <h3 className="text-3xl font-bold text-gray-900">1 Active Paid Pilot + 3 Letters of Intent</h3>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6 leading-relaxed text-center text-lg">
              Triality is being piloted in cardiovascular disease, one of the most pressing areas of active research. Our continuous evidence engine is currently deployed with cardiovascular AROs to optimize trial execution, prevent enrollment delays, and improve representation.
            </p>

            <div className="bg-green-100 border border-green-300 rounded-lg p-5">
              <p className="text-sm text-green-900 text-center">
                <strong>Focus Area:</strong> Cardiovascular disease trials represent a critical testing ground for continuous 
                evidence approaches. High event variability, complex enrollment patterns, and diversity requirements make this 
                therapeutic area ideal for demonstrating Triality's value in preventing costly delays and ensuring trial success.
              </p>
            </div>
          </div>
        </div>

        {/* Business Model Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold mb-4">
              <DollarSign className="w-5 h-5" />
              Business Model
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Scalable B2B SaaS for Clinical Trials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Predictive, continuous evidence platform designed for sustainable growth
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-200">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Customer Segments</h3>
                <p className="text-gray-600 text-sm">
                  Academic Research Organizations (current focus), expanding to pharmaceutical and medical device sponsors
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Revenue Model</h3>
                <p className="text-gray-600 text-sm">
                  Paid pilots ($10–25K per trial) → Recurring subscriptions ($50–150K annually) for portfolio-wide access
                </p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Go-to-Market</h3>
                <p className="text-gray-600 text-sm">
                  Direct, relationship-driven sales and strategic partnerships with AROs and industry sponsors
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Competitive Differentiation</h3>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                <p className="text-gray-800 leading-relaxed">
                  Triality differentiates itself from static feasibility tools and retrospective dashboards by providing 
                  <strong> continuous, predictive modeling</strong> that updates trial assumptions in real time, allowing sponsors 
                  to identify risk early and avoid costly delays and protocol amendments.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Market Opportunity</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-gray-900 to-blue-900 text-white rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold mb-2">$50B</p>
                  <p className="text-sm text-blue-200">Annual clinical trial operations spend</p>
                </div>
                <div className="bg-gradient-to-br from-red-600 to-orange-600 text-white rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold mb-2">$6–8B</p>
                  <p className="text-sm text-orange-200">Lost to inefficiencies Triality addresses</p>
                </div>
                <div className="bg-gradient-to-br from-green-600 to-emerald-600 text-white rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold mb-2">1–3%</p>
                  <p className="text-sm text-green-200">Target market capture within 3–5 years</p>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-5">
                <p className="text-sm text-blue-900 text-center">
                  <strong>Scalability:</strong> Low-marginal-cost software model enables sustainable, high-margin growth 
                  as we expand from cardiovascular to additional therapeutic areas.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet the Founder</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built by a clinical trialist who lived these challenges firsthand
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-200">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-1">
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl aspect-square flex items-center justify-center mb-6">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-blue-600 mb-2">PG</div>
                    <p className="text-sm text-gray-600">Prakriti Gaba, MD MPH</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Interventional Cardiologist</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Clinical Trialist</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">50+ Publications</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">MPH, Harvard T.H. Chan School</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">About Dr. Gaba</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Prakriti Gaba, MD MPH, is an interventional cardiologist and clinical trialist who founded Triality after 
                    repeatedly witnessing the same costly pattern across cardiovascular studies: feasibility assumptions that 
                    diverged from reality, event rates that shifted mid-study, and persistent underrepresentation of women. 
                    During her research fellowship at leading academic research organizations and throughout her training, she 
                    saw these issues slow enrollment, trigger amendments, and jeopardize statistical power in both academic and 
                    industry-sponsored trials. She created Triality to bring continuous evidence, adaptive feasibility intelligence, 
                    and enrollment equity into trial design—equipping sponsors with real-time tools that anticipate challenges 
                    before they occur and ensure studies are both efficient and truly representative of the patients they aim to serve.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <p className="text-gray-800 leading-relaxed italic">
                    "Clinical trials need real-time intelligence, not static assumptions. Triality was built to solve the 
                    problems I saw every day as a trialist—before they derail the studies that could transform patient care."
                  </p>
                  <p className="text-sm text-gray-600 mt-3">— Dr. Prakriti Gaba, Founder</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Clinical Trials?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join leading sponsors who are replacing static assumptions with adaptive intelligence
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all text-lg">
              Schedule a Demo
            </button>
            <button className="bg-blue-500/30 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-500/40 transition-all border-2 border-white/30 text-lg">
              Download White Paper
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="w-6 h-6 text-blue-400" />
            <span className="text-lg font-semibold text-white">Triality</span>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li>Platform Overview</li>
                <li>Features</li>
                <li>Pricing</li>
                <li>Case Studies</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>About Us</li>
                <li>Team</li>
                <li>Careers</li>
                <li>Contact</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li>Documentation</li>
                <li>White Papers</li>
                <li>Blog</li>
                <li>Webinars</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>Privacy Policy</li>
                <li>Terms of Service</li>
                <li>HIPAA Compliance</li>
                <li>Security</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            <p>© 2025 Triality. All rights reserved. Founded by Dr. Prakriti Gaba.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdaptiveTrialsWebsite;